<template>
  <div class="desktop-only">
    <NavPage v-if="!isMapFullscreen" />
    <HeaderPage v-if="!isMapFullscreen" />
  </div>
  <div class="mobile-only mb-4">
    <!-- <MobileHeader v-if="!isMapFullscreen && isMobile" /> -->
    <MobileHeader
      v-if="!isMapFullscreen && isMobile"
      :accepted-order="acceptedOrderDetails"
    />
  </div>
  <section class="account-page">
    <!-- <div class="container d-flex justify-content-center align-items-center bg-light"> -->
    <div
      class="container d-flex justify-content-center align-items-start bg-light min-vh-100 py-4"
    >
      <div
        class="card shadow-lg p-4 border-0 scrollable-card"
        style="max-width: 500px; width: 100%; border-radius: 1rem"
      >
        <div
          class="card shadow-lg p-4 border-0"
          style="max-width: 500px; width: 100%; border-radius: 1rem"
        >
          <h2 class="text-center mb-4 text-panda">{{ $t("profile edit") }}</h2>

          <div v-if="loading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ $t("loading...") }}</span>
            </div>
          </div>

          <div v-if="error" class="alert alert-danger">{{ error }}</div>
          <div v-if="success" class="alert alert-success">{{ success }}</div>

          <!-- Compression progress -->
          <div v-if="compressionProgress > 0" class="alert alert-info">
            Compressing image: {{ compressionProgress }}%
          </div>

          <form @submit.prevent="submitForm" v-if="!loading">
            <div class="mb-3">
              <label for="name" class="form-label">{{ $t("name") }}</label>
              <input
                v-model="form.name"
                type="text"
                id="name"
                class="form-control rounded-pill"
                required
              />
            </div>

            <div class="mb-3">
              <label for="email" class="form-label">{{ $t("email") }}</label>
              <input
                v-model="form.email"
                type="email"
                id="email"
                class="form-control rounded-pill"
              />
            </div>

            <div class="mb-3">
              <label for="phone" class="form-label">{{ $t("phone") }}</label>
              <input
                v-model="form.phone"
                type="tel"
                id="phone"
                class="form-control rounded-pill"
                required
              />
            </div>

            <div class="mb-3">
              <label for="image" class="form-label">{{ $t("image") }}</label>
              <input
                type="file"
                id="image"
                class="form-control rounded-pill"
                @change="handleImageChange"
                accept="image/*"
                :disabled="isCompressing"
              />
              <!-- File size info -->
              <small class="text-muted">
                {{ $t("Maximum file size: 50MB") }}
              </small>
              <div v-if="imagePreview" class="mt-2">
                <img
                  :src="imagePreview"
                  class="img-thumbnail"
                  alt="Preview"
                  style="max-height: 100px"
                />
              </div>
              <div v-else-if="currentImage" class="mt-2">
                <p>{{ $t("Current Image") }}:</p>
                <img
                  :src="currentImage"
                  class="img-thumbnail"
                  alt="Current"
                  style="max-height: 100px"
                />
              </div>
            </div>

            <div class="mb-4">
              <label for="password" class="form-label">{{ $t("password (new)") }}</label>
              <input
                v-model="form.password"
                type="password"
                id="password"
                class="form-control rounded-pill"
              />
              <small class="text-muted">{{
                $t("Leave blank to keep current password")
              }}</small>
            </div>

            <button
              type="submit"
              class="btn rounded-pill w-100 text-white"
              style="background-color: #d70f64"
              :disabled="submitting || isCompressing"
            >
              <span
                v-if="submitting || isCompressing"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {{ submitting ? "Saving..." : isCompressing ? "Compressing..." : "Save" }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </section>
  <FooterBarPage></FooterBarPage>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import FooterBarPage from "../layout/FooterBarPage.vue";
import MobileHeader from "../header/MobileHeader.vue";
import HeaderPage from "../header/HeaderPage.vue";
import { LOCAL_STORAGE_KEYS } from "@/utils/constants";
import axios from "@/api";
import imageCompression from "browser-image-compression";
import NavPage from "../navigation/NavPage.vue";

const imagePreview = ref(null);
const currentImage = ref(null);
const loading = ref(true);
const submitting = ref(false);
const error = ref(null);
const success = ref(null);
const isCompressing = ref(false);
const compressionProgress = ref(0);

const image = ref(null);

const form = reactive({
  name: "",
  email: "",
  phone: "",
  password: "",
});

// Handle image change with auto-compression
const handleImageChange = async (e) => {
  const file = e.target.files[0];
  if (!file) {
    image.value = null;
    imagePreview.value = null;
    return;
  }

  // Check if file is an image
  if (!file.type.startsWith("image/")) {
    error.value = "Please select a valid image file.";
    e.target.value = "";
    return;
  }

  // Check file size (50MB limit)
  const maxSize = 50 * 1024 * 1024; // 50MB in bytes
  if (file.size > maxSize) {
    error.value = "File size must be less than 50MB.";
    e.target.value = "";
    return;
  }

  // Create preview immediately
  const reader = new FileReader();
  reader.onload = (event) => {
    imagePreview.value = event.target.result;
  };
  reader.readAsDataURL(file);

  // Auto-compress the image
  try {
    isCompressing.value = true;
    error.value = null;

    const compressedFile = await compressImage(file);
    image.value = compressedFile;

    console.log("Image compressed successfully!");
  } catch (err) {
    console.error("Compression failed:", err);
    error.value = "Failed to compress image. Using original file.";
    image.value = file; // Fallback to original file

    setTimeout(() => {
      error.value = null;
    }, 3000);
  } finally {
    isCompressing.value = false;
  }
};

// Image compression function
const compressImage = async (file) => {
  const options = {
    maxSizeMB: 0.05, // Maximum size in MB (50KB)
    maxWidthOrHeight: 800, // Maximum width or height
    useWebWorker: true,
    fileType: file.type,
    onProgress: (progress) => {
      compressionProgress.value = Math.round(progress);
    },
  };

  try {
    compressionProgress.value = 0;

    const compressedFile = await imageCompression(file, options);

    console.log("Original file size:", (file.size / 1024 / 1024).toFixed(2), "MB");
    console.log("Compressed file size:", (compressedFile.size / 1024).toFixed(2), "KB");

    return compressedFile;
  } catch (err) {
    console.error("Image compression error:", err);
    throw new Error("Failed to compress image");
  } finally {
    compressionProgress.value = 0;
  }
};

onMounted(async () => {
  try {
    // ✅ Load cached data first
    const cached = localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_USER_INFO);
    if (cached) {
      const user = JSON.parse(cached);
      form.name = user.name;
      form.email = user.email;
      form.phone = user.phone ?? "";
      if (user.image) {
        currentImage.value = `https://api.axefood.org//storage/${user.image}`;
      }
      loading.value = false; // ✅ don't block UI
    }

    // ✅ Fetch fresh data in background
    const response = await axios.get("/deli_profile_edit", { withCredentials: true });
    const user = response.data;

    form.name = user.name;
    form.email = user.email;
    form.phone = user.phone ?? "";
    if (user.image) {
      currentImage.value = `https://api.axefood.org//storage/${user.image}`;
    }

    localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH_USER_INFO, JSON.stringify(user));
  } catch (err) {
    error.value = "Failed to load user data. Please try again later.";
    console.error("Failed to load user data:", err);
  } finally {
    loading.value = false;
  }
});

const submitForm = async () => {
  error.value = null;
  success.value = null;
  submitting.value = true;

  try {
    const formData = new FormData();
    formData.append("name", form.name);

    // Only append email if it's not empty
    if (form.email && form.email.trim() !== "") {
      formData.append("email", form.email);
    }

    formData.append("phone", form.phone);

    if (form.password && form.password.trim() !== "") {
      formData.append("password", form.password);
    }

    if (image.value) {
      formData.append("image", image.value);
    }

    // Use the correct endpoint - get user ID from localStorage
    const userInfo = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_USER_INFO) || "{}"
    );
    const userId = userInfo.id;

    if (!userId) {
      throw new Error("User ID not found. Please log in again.");
    }

    await axios.post(`/deli_profile_update/${userId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      withCredentials: true,
    });

    // Refresh user data
    const response = await axios.get("/deli_profile_edit", {
      withCredentials: true,
    });
    const user = response.data;

    // Update localStorage
    localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH_USER_INFO, JSON.stringify(user));

    // Update image display
    if (user.image) {
      // Handle both full URL and relative path
      if (user.image.startsWith("http")) {
        currentImage.value = user.image;
      } else {
        currentImage.value = `https://api.axefood.org/storage/${user.image}`;
      }
    }
    imagePreview.value = null;
    image.value = null;

    success.value = "Profile updated successfully!";

    // Clear success message after 3 seconds
    setTimeout(() => {
      success.value = null;
    }, 3000);
  } catch (err) {
    console.error("Update failed:", err);

    // Better error handling
    if (err.response?.status === 422) {
      // Validation errors
      const errors = err.response.data.errors;
      error.value = Object.values(errors).flat().join(", ");
    } else if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else if (err.message) {
      error.value = err.message;
    } else {
      error.value = "Failed to update profile. Please try again.";
    }

    // Clear error after 5 seconds
    setTimeout(() => {
      error.value = null;
    }, 5000);
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped>
.account-page {
  background-color: #f8f9fa;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  border-radius: 30px;
  margin-top: -20px;
  z-index: 1;
  position: relative;
}

.text-panda {
  color: #d70f64;
  font-weight: bold;
}

.alert {
  padding: 10px 15px;
  margin-bottom: 15px;
  border-radius: 8px;
  font-size: 14px;
}

.alert-info {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.alert-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.text-muted {
  font-size: 0.875em;
  color: #6c757d;
  display: block;
  margin-top: 5px;
}

.btn:disabled {
  background-color: #cccccc !important;
  cursor: not-allowed;
  opacity: 0.6;
}

.img-thumbnail {
  border-radius: 8px;
  border: 1px solid #ddd;
}
</style>
